/**
 * AgentMeter SDK TypeScript Type Definitions
 * Contains all interfaces and types for the AgentMeter API integration
 */

/**
 * Standard API response wrapper used by all AgentMeter endpoints
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
  };
}

/**
 * Application entity representing a registered app in AgentMeter
 */
export interface App {
  id: string;
  merchant_id: string;
  name: string;
  description?: string;
  api_key: string;
  webhook_url?: string;
  created_at: string;
  updated_at: string;
  status: 'active' | 'inactive';
  meters?: { count: number }[];
  app_users?: { count: number }[];
}

/**
 * Meter entity defining how events are aggregated and billed
 */
export interface Meter {
  id: string;
  app_id: string;
  name: string;
  description?: string;
  event_name: string;
  aggregation_method: 'SUM' | 'COUNT' | 'UNIQUE_COUNT' | 'AVG' | 'MAX' | 'MIN';
  aggregation_key?: string;
  group_by?: string[];
  price_per_unit: number;
  currency: 'USD' | 'EUR' | 'GBP' | 'CNY';
  billing_cycle: 'hourly' | 'daily' | 'weekly' | 'monthly';
  created_at: string;
  updated_at: string;
  status: 'active' | 'inactive';
}

/**
 * Event entity representing a tracked user action or API call
 */
export interface Event {
  id: string;
  app_id: string;
  event_name: string;
  user_id?: string;
  properties: Record<string, any>;
  value?: number;
  timestamp: string;
  created_at: string;
}

/**
 * Usage entity representing aggregated usage data for billing
 */
export interface Usage {
  id: string;
  meter_id: string;
  app_id: string;
  user_id?: string;
  period_start: string;
  period_end: string;
  aggregated_value: number;
  group_values?: Record<string, any>;
  cost: number;
  created_at: string;
}

/**
 * Usage summary providing comprehensive billing overview
 */
export interface UsageSummary {
  app_id: string;
  meters: {
    meter: Meter;
    total_usage: number;
    total_cost: number;
    usage_records: number;
  }[];
  summary: {
    total_cost: number;
    total_meters: number;
    active_users: number;
    total_events: number;
  };
}

/**
 * Time series usage data for charts and analytics
 */
export interface UsageTimeSeries {
  meter_id: string;
  interval: 'hour' | 'day' | 'week' | 'month';
  data_points: {
    timestamp: string;
    value: number;
    cost: number;
    user_count?: number;
  }[];
}

// Request Types

/**
 * Request payload for creating a new application
 */
export interface CreateAppRequest {
  name: string;
  description?: string;
  webhook_url?: string;
}

/**
 * Request payload for updating an existing application
 */
export interface UpdateAppRequest {
  name?: string;
  description?: string;
  webhook_url?: string;
  status?: 'active' | 'inactive';
}

/**
 * Request payload for creating a new meter
 */
export interface CreateMeterRequest {
  name: string;
  description?: string;
  event_name: string;
  aggregation_method: 'SUM' | 'COUNT' | 'UNIQUE_COUNT' | 'AVG' | 'MAX' | 'MIN';
  aggregation_key?: string;
  group_by?: string[];
  price_per_unit: number;
  currency: 'USD' | 'EUR' | 'GBP' | 'CNY';
  billing_cycle: 'hourly' | 'daily' | 'weekly' | 'monthly';
  app_id?: string;
}

/**
 * Request payload for updating an existing meter
 */
export interface UpdateMeterRequest {
  name?: string;
  description?: string;
  aggregation_method?: 'SUM' | 'COUNT' | 'UNIQUE_COUNT' | 'AVG' | 'MAX' | 'MIN';
  aggregation_key?: string;
  group_by?: string[];
  price_per_unit?: number;
  currency?: 'USD' | 'EUR' | 'GBP' | 'CNY';
  billing_cycle?: 'hourly' | 'daily' | 'weekly' | 'monthly';
  status?: 'active' | 'inactive';
}

/**
 * Request payload for tracking a single event
 */
export interface CreateEventRequest {
  event_name: string;
  user_id?: string;
  properties?: Record<string, any>;
  value?: number;
  timestamp?: string;
}

// Query Parameter Types

/**
 * Query parameters for fetching events
 */
export interface EventsQueryParams {
  app_id?: string;
  event_name?: string;
  user_id?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

/**
 * Query parameters for fetching usage data
 */
export interface UsageQueryParams {
  app_id: string;
  meter_id?: string;
  user_id?: string;
  period_start?: string;
  period_end?: string;
  group_by?: string;
}

/**
 * Query parameters for fetching usage timeseries
 */
export interface UsageTimeSeriesParams {
  meter_id: string;
  interval?: 'hour' | 'day' | 'week' | 'month';
  start_date?: string;
  end_date?: string;
}

// Configuration Types

/**
 * SDK configuration options
 */
export interface AgentMeterConfig {
  apiKey: string;
  baseUrl?: string;
  timeout?: number;
  retryAttempts?: number;
  retryDelay?: number;
  debug?: boolean;
}

// Error Types

/**
 * Custom error class for AgentMeter API errors
 */
export class AgentMeterError extends Error {
  public readonly code: string;
  public readonly statusCode?: number;
  public readonly details?: any;

  constructor(message: string, code: string, statusCode?: number, details?: any) {
    super(message);
    this.name = 'AgentMeterError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
    
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AgentMeterError);
    }
  }
}

/**
 * Validation error for request parameters
 */
export class ValidationError extends AgentMeterError {
  constructor(message: string, details?: any) {
    super(message, 'VALIDATION_ERROR', 400, details);
    this.name = 'ValidationError';
  }
}

/**
 * Network error for connection issues
 */
export class NetworkError extends AgentMeterError {
  constructor(message: string, details?: any) {
    super(message, 'NETWORK_ERROR', undefined, details);
    this.name = 'NetworkError';
  }
}